import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MdShoppingCart, MdDelete, MdRemove, MdAdd } from "react-icons/md";

import styles from "./Cart.module.scss";
import { useCart } from "./useCart";
import { PageHeader } from "../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import { getClusterImageUrl } from "../../common/utils/functions/getImageUri";
import { CHECKOUT_ROUTE } from "../../common/utils/constants";

export const Cart: React.FC = () => {
  const navigate = useNavigate();
  const {
    grouped,
    selectedClusterId,
    setSelectedClusterId,
    selectedGroup,
    selectedCutleryCount,
    isEmpty,
    increase,
    decrease,
    remove,
    incCutlery,
    decCutlery,
  } = useCart();

  if (isEmpty) {
    return (
      <div className={styles.Page}>
        <Helmet>
          <title>Корзина — ЛОГМОЛЛ</title>
        </Helmet>
        <PageHeader title="Корзина" />
        <EmptyState
          icon={MdShoppingCart}
          title="Корзина пуста"
          description="Добавьте товары из каталога, чтобы оформить заказ."
        />
      </div>
    );
  }

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>Корзина — ЛОГМОЛЛ</title>
      </Helmet>

      <PageHeader title="Корзина" />

      <div className={styles.Layout}>
        <aside className={styles.ClusterList}>
          {grouped.map((group) => {
            const isActive = selectedClusterId === group.clusterId;
            const cluster = group.cluster;
            return (
              <button
                key={group.clusterId}
                type="button"
                className={styles.ClusterCard}
                data-active={isActive || undefined}
                onClick={() => setSelectedClusterId(group.clusterId)}
              >
                {cluster && (
                  <img
                    src={`https://${cluster.domain}/icons/logo512.png`}
                    alt={cluster.name}
                    className={styles.ClusterLogo}
                  />
                )}
                <div className={styles.ClusterInfo}>
                  <span className={styles.ClusterName}>
                    {cluster?.name || `Кластер #${group.clusterId}`}
                  </span>
                  <span className={styles.ClusterMeta}>
                    {group.items.length} товар(ов) · {group.total} ₽
                  </span>
                </div>
              </button>
            );
          })}
        </aside>

        <section className={styles.ItemsSection}>
          {!selectedGroup ? (
            <EmptyState
              icon={MdShoppingCart}
              title="Выберите компанию"
              description="Выберите компанию слева, чтобы увидеть товары."
            />
          ) : (
            <>
              <h2 className={styles.SectionTitle}>
                {selectedGroup.cluster?.name}
              </h2>

              <div className={styles.ItemsList}>
                {selectedGroup.items.map((item) => {
                  const cluster = selectedGroup.cluster;
                  const image =
                    item.image && cluster
                      ? getClusterImageUrl(cluster, item.image)
                      : null;

                  return (
                    <div key={item.id} className={styles.Row}>
                      {image ? (
                        <img
                          src={image}
                          alt={item.name}
                          className={styles.RowImage}
                        />
                      ) : (
                        <div className={styles.RowImagePlaceholder} />
                      )}

                      <div className={styles.RowInfo}>
                        <span className={styles.RowName}>{item.name}</span>
                        <span className={styles.RowPrice}>{item.price} ₽</span>
                      </div>

                      <div className={styles.RowActions}>
                        <button
                          type="button"
                          className={styles.IconButton}
                          onClick={() =>
                            item.quantity === item.minQuantity
                              ? remove(item.id)
                              : decrease(item.id)
                          }
                          aria-label="Уменьшить"
                        >
                          {item.quantity === item.minQuantity ? (
                            <MdDelete size={22} />
                          ) : (
                            <MdRemove size={22} />
                          )}
                        </button>
                        <span className={styles.QuantityText}>
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          className={styles.IconButton}
                          onClick={() => increase(item.id)}
                          aria-label="Увеличить"
                        >
                          <MdAdd size={22} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedGroup.cluster?.cutlery && (
                <div className={styles.CutleryRow}>
                  <span className={styles.CutleryLabel}>Приборы</span>
                  <div className={styles.RowActions}>
                    <button
                      type="button"
                      className={styles.IconButton}
                      onClick={() => decCutlery(selectedGroup.clusterId)}
                      aria-label="Меньше приборов"
                    >
                      <MdRemove size={22} />
                    </button>
                    <span className={styles.QuantityText}>
                      {selectedCutleryCount}
                    </span>
                    <button
                      type="button"
                      className={styles.IconButton}
                      onClick={() => incCutlery(selectedGroup.clusterId)}
                      aria-label="Больше приборов"
                    >
                      <MdAdd size={22} />
                    </button>
                  </div>
                </div>
              )}

              <button
                type="button"
                className={styles.CheckoutButton}
                onClick={() =>
                  navigate(
                    `${CHECKOUT_ROUTE}?clusterId=${selectedGroup.clusterId}`,
                  )
                }
              >
                <span className={styles.CheckoutBg} aria-hidden="true">
                  Оформить
                </span>
                <span className={styles.CheckoutLabel}>Оформить</span>
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};
